<template>
  <div>
    <v-container class="toolbar__container">
      <v-row>
        <v-col cols="12" sm="8" md="5">
          <v-autocomplete
            v-model="idEquipamento"
            :items="equipamentosComInventario"
            :item-text="rotuloEquipamento"
            item-value="id_equipamento"
            clearable
            :label="$t('statusDispositivos.filters.equipment')"
            :placeholder="$t('statusDispositivos.messages.allEquipments')"
            persistent-placeholder
          />
        </v-col>
        <v-col cols="12" sm="4" md="2" class="d-flex align-center">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn small fab color="primary darken-1" class="white--text" v-bind="attrs" v-on="on" :loading="loading" @click="popula">
                <v-icon>mdi-magnify</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('statusDispositivos.actions.search') }}</span>
          </v-tooltip>
          <!-- HikCentral não expõe o aparelho: o que se lista para ele é o nível de acesso. -->
          <AjudaTooltip v-if="avisoNivelAcesso" class="ml-2" :texto="avisoNivelAcesso" />
        </v-col>
      </v-row>
    </v-container>

    <v-card class="mt-2" style="border-radius: 10px">
      <v-tabs v-model="tab" background-color="transparent" color="primary">
        <v-tab>{{ tituloPessoas }}</v-tab>
        <v-tab v-if="mostraCartoes">{{ $t('statusDispositivos.tabs.cards') }}</v-tab>
      </v-tabs>
      <v-tabs-items v-model="tab">
        <v-tab-item>
          <merus-data-table
            :key="`${tableKey}-pessoas`"
            cod-tabela="EQUIPAMENTOS__VALIDADORES_PESSOAS"
            :headers="headersPessoas"
            :items="pessoas"
            :loading="loading"
            item-key="chave"
            @update:headers="headersPessoas = $event"
          >
            <template v-slot:toolbar>
              <CadastroToolbar
                :title="tituloPessoas"
                :token="token"
                :items="pessoas"
                csv-title="dispositivos_pessoas"
                :csv-headers="headersPessoas"
                :pdf-title="tituloPessoas"
                :pdf-headers="headersPessoas"
                pdf-filename="dispositivos_pessoas"
                hide-search
              />
            </template>
          </merus-data-table>
        </v-tab-item>
        <v-tab-item v-if="mostraCartoes">
          <merus-data-table
            :key="`${tableKey}-cartoes`"
            cod-tabela="EQUIPAMENTOS__VALIDADORES_CARTOES"
            :headers="headersCartoes"
            :items="cartoes"
            :loading="loading"
            item-key="chave"
            @update:headers="headersCartoes = $event"
          >
            <template v-slot:toolbar>
              <CadastroToolbar
                :title="$t('statusDispositivos.tabs.cards')"
                :token="token"
                :items="cartoes"
                csv-title="dispositivos_cartoes"
                :csv-headers="headersCartoes"
                :pdf-title="$t('statusDispositivos.tabs.cards')"
                :pdf-headers="headersCartoes"
                pdf-filename="dispositivos_cartoes"
                hide-search
              />
            </template>
          </merus-data-table>
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </div>
</template>

<script>
import apiController from '@/utils/apiController';
import MerusDataTable from '../../Tables/MerusDataTable.vue';
import CadastroToolbar from '../../Toolbars/CadastroToolbar.vue';
import dispositivosFormatters from './dispositivosFormatters';
import recorteEquipamentos from './recorteEquipamentos';
import AjudaTooltip from '../AjudaTooltip.vue';

export default {
  name: 'EmbarquePermitido',
  components: { MerusDataTable, CadastroToolbar, AjudaTooltip },
  mixins: [dispositivosFormatters, recorteEquipamentos],
  props: {
    equipamentos: { type: Array, default: () => [] },
    token: { type: String, default: null },
  },
  data: () => ({
    tab: 0,
    idEquipamento: null,
    loading: false,
    pessoas: [],
    cartoes: [],
    headersPessoas: [],
    headersCartoes: [],
  }),
  computed: {
    // Estes módulos vivem do inventário: do aparelho (ISAPI) ou do nível de acesso
    // (HikCentral). Suite sem inventário nunca teria linha e só poluiria o filtro.
    equipamentosComInventario() {
      return (this.equipamentos || []).filter((e) => e.suporta_inventario == 1);
    },
    equipamentoSelecionado() {
      if (this.idEquipamento == null) return null;
      return (this.equipamentos || []).find((e) => e.id_equipamento === this.idEquipamento) || null;
    },
    selecionadoNoNivel() {
      return !!this.equipamentoSelecionado && this.equipamentoSelecionado.fonte_inventario === 'NIVEL_ACESSO';
    },
    tituloPessoas() {
      return this.selecionadoNoNivel
        ? this.$t('statusDispositivos.inventory.peopleInAccessLevel')
        : this.$t('statusDispositivos.tabs.people');
    },
    // Cartões só existem no inventário do aparelho.
    mostraCartoes() {
      return !this.selecionadoNoNivel;
    },
    avisoNivelAcesso() {
      if (this.selecionadoNoNivel) return this.$t('statusDispositivos.inventory.accessLevelNotice');
      if (this.equipamentoSelecionado) return '';
      const algum = (this.equipamentos || []).some((e) => e.fonte_inventario === 'NIVEL_ACESSO');
      return algum ? this.$t('statusDispositivos.inventory.mixedFleetNotice') : '';
    },
    tableKey() {
      return `dispositivos-embarque-${this.$i18n.locale}`;
    },
  },
  watch: {
    '$i18n.locale'() {
      this.initializeHeaders();
    },
    // A aba de cartões some para leitor HikCentral: não deixar a seleção apontando para ela.
    mostraCartoes(mostra) {
      if (!mostra) this.tab = 0;
    },
  },
  created() {
    this.initializeHeaders();
    this.popula();
  },
  methods: {
    rotuloEquipamento(item) {
      return item.cod_veiculo ? `${item.nome_equipamento} (${item.cod_veiculo})` : item.nome_equipamento;
    },
    initializeHeaders() {
      const h = this.$t.bind(this);
      this.headersPessoas = [
        { text: h('statusDispositivos.headers.equipment'), value: 'nome_equipamento', sortable: true, filtrable: true },
        { text: h('statusDispositivos.headers.vehicle'), value: 'cod_veiculo', sortable: true, filtrable: true },
        { text: h('statusDispositivos.headers.registrationCode'), value: 'matricula', sortable: true, filtrable: true },
        { text: h('statusDispositivos.headers.personName'), value: 'nome_completo', sortable: true, filtrable: true },
        { text: h('statusDispositivos.headers.integrationCode'), value: 'cod_pessoa_dispositivo', sortable: true, filtrable: true },
        { text: h('statusDispositivos.headers.deviceName'), value: 'nome_dispositivo', sortable: true, filtrable: true },
        { text: h('statusDispositivos.headers.authorized'), value: 'autorizado_extenso', sortable: true, filtrable: true },
        { text: h('statusDispositivos.headers.syncedAt'), value: 'dt_sync_extenso', sortable: true, filtrable: true },
      ];
      this.headersCartoes = [
        { text: h('statusDispositivos.headers.equipment'), value: 'nome_equipamento', sortable: true, filtrable: true },
        { text: h('statusDispositivos.headers.cardNo'), value: 'card_no', sortable: true, filtrable: true },
        { text: h('statusDispositivos.headers.personName'), value: 'nome_completo', sortable: true, filtrable: true },
        { text: h('statusDispositivos.headers.cardNoMerus'), value: 'card_no_merus', sortable: true, filtrable: true },
        { text: h('statusDispositivos.headers.qrcodeNo'), value: 'qrcode_no', sortable: true, filtrable: true },
        { text: h('statusDispositivos.headers.syncedAt'), value: 'dt_sync_extenso', sortable: true, filtrable: true },
      ];
    },
    popula() {
      this.loading = true;
      // id_equipamento omitido = frota inteira (o router aceita nulo).
      const filtro = this.idEquipamento != null ? { id_equipamento: this.idEquipamento } : {};
      Promise.all([
        apiController.request(apiController.endpoints.isapiGetIsapipessoas, { data: filtro }),
        apiController.request(apiController.endpoints.isapiGetIsapicartoes, { data: filtro }),
      ])
        .then(([pessoasRes, cartoesRes]) => {
          this.pessoas = this.recortaDaLista(pessoasRes.data || []).map((p) => ({
            ...p,
            chave: `${p.id_equipamento}-${p.id_pessoa}`,
            autorizado_extenso: this.fmtSimNao(p.autorizado_faceid),
            dt_sync_extenso: this.fmtDataHora(p.dt_sync),
          }));
          this.cartoes = this.recortaDaLista(cartoesRes.data || []).map((c) => ({
            ...c,
            chave: `${c.id_equipamento}-${c.card_no}`,
            dt_sync_extenso: this.fmtDataHora(c.dt_sync),
          }));
        })
        .catch(() => {
          this.pessoas = [];
          this.cartoes = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>
